import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Button, Chip, Alert, Snackbar, CircularProgress, Tooltip, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText, TextField,
} from '@mui/material';
import {
  UploadFile as UploadIcon, CheckCircle as CheckIcon, Block as BlockIcon,
  ShieldOutlined as ShieldIcon, DownloadOutlined as DownloadIcon,
} from '@mui/icons-material';
import { keyboxService, KeyboxItem } from '../../services/keyboxService';
import { EmptyState, PageHeader, StatusChip } from '../ui';

const shortFp = (fp: string | null) => (fp ? `${fp.slice(0, 10)}…${fp.slice(-6)}` : '—');
const fmtTime = (ms: number | null) => (ms ? new Date(ms).toLocaleString() : '—');

export const KeyboxManagement: React.FC = () => {
  const [items, setItems] = useState<KeyboxItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Export yêu cầu nhập lại mật khẩu, nên phải giữ keybox đang chờ tải.
  const [exportFor, setExportFor] = useState<KeyboxItem | null>(null);
  const [exportPassword, setExportPassword] = useState('');
  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await keyboxService.list());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setBusy('upload');
    try {
      const xml = await file.text();
      const label = file.name.replace(/\.xml$/i, '');
      const r = await keyboxService.upload(xml, label);
      if (r.result) {
        setOk(`Đã nạp keybox "${label}" (EC ${r.ecFingerprint?.slice(0, 10)}…). Bấm "Set active" để dùng.`);
        await load();
      } else {
        setError(r.message || 'Upload thất bại');
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const setActive = async (id: string, slot: 'EC' | 'RSA' | 'both') => {
    setBusy(id + slot);
    try {
      const r = await keyboxService.setActive(id, slot);
      r.result ? setOk(r.message || 'Đã đặt active') : setError(r.message || 'Thất bại');
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const closeExport = () => {
    setExportFor(null);
    setExportPassword('');
    setExportError(null);
  };

  /** Đẩy chuỗi XML xuống máy qua blob URL — không đi qua server lần nữa. */
  const saveFile = (filename: string, xml: string) => {
    const url = URL.createObjectURL(new Blob([xml], { type: 'application/xml' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const doExport = async () => {
    if (!exportFor || !exportPassword) return;
    setExporting(true);
    setExportError(null);
    try {
      const r = await keyboxService.exportXml(exportFor.id, exportPassword);
      if (!r.result || !r.xml) {
        setExportError(r.message || 'Export thất bại');
        return;
      }
      saveFile(r.filename || 'keybox.xml', r.xml);
      const left = typeof r.remainingToday === 'number' ? ` (còn ${r.remainingToday} lần trong giờ này)` : '';
      setOk(`Đã tải keybox.xml của "${exportFor.label || exportFor.id.slice(0, 8)}"${left}`);
      closeExport();
    } catch (e) {
      setExportError((e as Error).message);
    } finally {
      setExporting(false);
    }
  };

  const revoke = async (id: string) => {
    if (!window.confirm('Revoke keybox này? (không dùng để ký nữa)')) return;
    setBusy(id + 'revoke');
    try {
      const r = await keyboxService.revoke(id);
      r.result ? setOk(r.message || 'Đã revoke') : setError(r.message || 'Thất bại');
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Config"
        title="Keyboxes"
        description="Nạp nhiều keybox.xml, chọn keybox active để ký attestation. Private key mã hoá trong DB, không hiển thị. Đổi active có hiệu lực ngay — không cần rebuild module."
        actions={
          <>
            <Button
              variant="contained"
              startIcon={busy === 'upload' ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
              disabled={busy === 'upload'}
              onClick={() => fileRef.current?.click()}
            >
              Nạp keybox.xml
            </Button>
            <input ref={fileRef} type="file" accept=".xml,text/xml" hidden onChange={onUpload} />
          </>
        }
      />

      <TableContainer component={Card}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Label</TableCell>
              <TableCell>EC fingerprint</TableCell>
              <TableCell>RSA fingerprint</TableCell>
              <TableCell>Trạng thái</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Nạp lúc</TableCell>
              <TableCell align="right">Thao tác</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={22} />
                </TableCell>
              </TableRow>
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} sx={{ p: 0 }}>
                  <EmptyState
                    icon={<ShieldIcon />}
                    title="Chưa có keybox nào"
                    description="Nạp một keybox.xml để bắt đầu ký attestation."
                  />
                </TableCell>
              </TableRow>
            ) : (
              items.map((k) => {
                const revoked = k.status === 'revoked';
                return (
                  <TableRow key={k.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{k.label || k.id.slice(0, 8)}</TableCell>
                    <TableCell><Tooltip title={k.ecFingerprint || ''}><span className="mono">{shortFp(k.ecFingerprint)}</span></Tooltip></TableCell>
                    <TableCell><Tooltip title={k.rsaFingerprint || ''}><span className="mono">{shortFp(k.rsaFingerprint)}</span></Tooltip></TableCell>
                    <TableCell>
                      <StatusChip label={k.status} tone={revoked ? 'error' : 'success'} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        {k.activeEc && <Chip size="small" icon={<CheckIcon />} label="EC" color="success" />}
                        {k.activeRsa && <Chip size="small" icon={<CheckIcon />} label="RSA" color="success" />}
                        {!k.activeEc && !k.activeRsa && <Typography variant="caption" color="text.secondary">—</Typography>}
                      </Stack>
                    </TableCell>
                    <TableCell>{fmtTime(k.uploadedAt)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" variant="outlined" disabled={revoked || busy === k.id + 'both'}
                          onClick={() => setActive(k.id, 'both')}>Set active</Button>
                        <Button size="small" disabled={revoked || busy === k.id + 'EC'}
                          onClick={() => setActive(k.id, 'EC')}>EC</Button>
                        <Button size="small" disabled={revoked || busy === k.id + 'RSA'}
                          onClick={() => setActive(k.id, 'RSA')}>RSA</Button>
                        <Tooltip title="Tải lại keybox.xml (cần nhập mật khẩu)">
                          <span>
                            <Button size="small" startIcon={<DownloadIcon />}
                              onClick={() => { setExportFor(k); setExportPassword(''); setExportError(null); }}>Tải về</Button>
                          </span>
                        </Tooltip>
                        <Tooltip title="Revoke (gỡ active trước)">
                          <span>
                            <Button size="small" color="error" startIcon={<BlockIcon />}
                              disabled={revoked || busy === k.id + 'revoke'} onClick={() => revoke(k.id)}>Revoke</Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!exportFor} onClose={exporting ? undefined : closeExport} maxWidth="sm" fullWidth>
        <DialogTitle>Tải lại keybox.xml</DialogTitle>
        <DialogContent>
          <DialogContentText component="div" sx={{ fontSize: '0.875rem' }}>
            Keybox <b>{exportFor?.label || exportFor?.id.slice(0, 8)}</b> sẽ được dựng lại thành
            file keybox.xml chứa <b>private key dạng plaintext</b>. Đây là đường duy nhất key rời
            khỏi server, nên mọi lần tải đều được ghi nhật ký kèm IP, và giới hạn 5 lần mỗi giờ.
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            File tải về tương đương về mặt mật mã nhưng KHÔNG trùng byte với bản gốc: DeviceID đặt
            cố định là "MC", và private key xuất theo định dạng chuẩn của keybox (SEC1/PKCS#1).
          </Alert>
          {exportError && <Alert severity="error" sx={{ mt: 2 }} role="alert">{exportError}</Alert>}
          <TextField
            fullWidth
            type="password"
            label="Mật khẩu tài khoản của bạn"
            autoComplete="current-password"
            autoFocus
            value={exportPassword}
            onChange={(e) => setExportPassword(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && exportPassword && !exporting) void doExport(); }}
            disabled={exporting}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeExport} disabled={exporting}>Huỷ</Button>
          <Button
            variant="contained"
            onClick={doExport}
            disabled={!exportPassword || exporting}
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
          >
            {exporting ? 'Đang dựng…' : 'Tải về'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!ok} autoHideDuration={5000} onClose={() => setOk(null)}>
        <Alert severity="success" onClose={() => setOk(null)}>{ok}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={7000} onClose={() => setError(null)}>
        <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
      </Snackbar>
    </Box>
  );
};
